import { ShieldCheck, ListChecks, AlertTriangle } from "lucide-react";

/**
 * Wiederverwendbare NBA-Zusammenfassung mit identischer UX im
 * Freitext-Analyse-Tab und im KI-Interview-Result-Screen.
 *
 * Props:
 *   tendenz: { tendenz_label, ampel, score_intern, schwere_treffer[], leichte_treffer[], disclaimer }
 *   modulesAll: [{ id, nummer, name, betroffen, matched_keywords[] }, ...]
 *   missingFragen: [{ topic, frage }, ...]
 *   onMissingClick?: (frage, topic) => void   // optional · falls weggelassen, sind die Chips read-only
 *   testIdPrefix?: string                     // default "pki-nba-summary"
 */
export default function PkiNbaSummary({
  tendenz,
  modulesAll,
  missingFragen,
  onMissingClick,
  testIdPrefix = "pki-nba-summary",
}) {
  return (
    <div className="space-y-4" data-testid={testIdPrefix}>
      {/* Pflegegrad-Tendenz Ampel-Pille (prominent) */}
      {tendenz && (
        <div className="rounded-2xl border border-cream-300 bg-white p-5"
          data-testid={`${testIdPrefix}-tendenz`}>
          <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-3">
            Pflegegrad-Tendenz · Heuristik
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span
              data-testid={`${testIdPrefix}-tendenz-pill`}
              className={`inline-flex items-center gap-2 px-5 h-11 rounded-full border-2 text-[14px] font-bold uppercase tracking-[0.12em] ${
                tendenz.ampel === "rot"
                  ? "bg-brand-darkred/15 text-brand-darkred border-brand-darkred/40"
                  : tendenz.ampel === "gelb"
                  ? "bg-saffron/25 text-clay-dark border-saffron/60"
                  : "bg-sage-dark/15 text-sage-dark border-sage-dark/40"
              }`}>
              <ShieldCheck className="h-4 w-4" strokeWidth={2.2} />
              {tendenz.tendenz_label}
            </span>
            {typeof tendenz.score_intern === "number" && (
              <span className="text-[11.5px] text-sand-500 font-mono">
                Score intern: {tendenz.score_intern}
              </span>
            )}
          </div>
          <p className="mt-2.5 text-[11.5px] text-sand-500 italic">
            Keine offizielle Einstufung. Die Bewertung erfolgt durch den Medizinischen Dienst.
          </p>
          {(tendenz.schwere_treffer?.length > 0 || tendenz.leichte_treffer?.length > 0) && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(tendenz.schwere_treffer || []).map((t) => (
                <span key={`s-${t}`}
                  className="inline-flex items-center gap-1 px-2.5 h-6 rounded-full bg-brand-darkred/15 text-brand-darkred text-[11px] font-semibold">
                  ● {t}
                </span>
              ))}
              {(tendenz.leichte_treffer || []).map((t) => (
                <span key={`l-${t}`}
                  className="inline-flex items-center gap-1 px-2.5 h-6 rounded-full bg-cream-200 text-sand-700 text-[11px] font-semibold">
                  ○ {t}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Alle 6 NBA-Module */}
      {modulesAll && modulesAll.length > 0 && (
        <div className="rounded-2xl border border-cream-300 bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <ListChecks className="h-4 w-4 text-clay-dark" strokeWidth={2} />
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
              NBA-Module · 6 Bereiche
              ({modulesAll.filter((m) => m.betroffen).length} betroffen)
            </div>
          </div>
          <ul className="grid md:grid-cols-2 gap-2.5">
            {modulesAll.map((m) => {
              const betroffen = m.betroffen !== false && (m.matched_keywords?.length || 0) > 0;
              return (
                <li key={m.id || m.name}
                  data-testid={`${testIdPrefix}-module-${m.id || m.nummer}`}
                  className={`rounded-xl border p-3 ${
                    betroffen
                      ? "border-terra/40 bg-peach-50"
                      : "border-cream-200 bg-cream-50/60"
                  }`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className={`text-[12.5px] font-bold ${betroffen ? "text-sand-800" : "text-sand-500"}`}>
                      Modul {m.nummer} · {m.name}
                    </div>
                    <span className={`inline-flex h-5 px-2 items-center rounded-full text-[9.5px] uppercase tracking-[0.18em] font-bold whitespace-nowrap ${
                      betroffen
                        ? "bg-terra text-cream-50"
                        : "bg-cream-200 text-sand-500"
                    }`}>
                      {betroffen ? "betroffen" : "unauffällig"}
                    </span>
                  </div>
                  {betroffen ? (
                    <div className="mt-1.5">
                      <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-clay-dark mb-1">
                        Gründe
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(m.matched_keywords || []).map((k) => (
                          <span key={k}
                            className="inline-block bg-white border border-terra/30 text-sand-800 rounded-full px-2 py-0.5 text-[11px] font-medium">
                            {k}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-1 text-[11.5px] text-sand-500 italic leading-snug">
                      Keine Trigger im Text — Pflegekraft prüft selbst, ob relevant.
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Fehlende Themen mit konkreten Folgefragen */}
      {missingFragen?.length > 0 && (
        <div className="rounded-2xl border border-terra/30 bg-peach-50 p-5"
          data-testid={`${testIdPrefix}-missing`}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-terra" strokeWidth={2} />
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-terra">
              Fehlende Informationen für Pflegegrad-Einschätzung ({missingFragen.length})
            </div>
          </div>
          <p className="text-[12.5px] text-sand-700 mb-3 leading-snug">
            Folgende Standard-Pflegethemen fehlen.{" "}
            {onMissingClick
              ? "Klick auf einen Bereich → konkrete Folgefrage wird übernommen:"
              : "Bitte vor MD-Begutachtung ergänzen:"}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {missingFragen.map((m) => {
              const slug = (m.topic || "").toLowerCase().replace(/[^a-z0-9]/g, "-");
              const baseCls = "inline-flex items-center gap-1.5 rounded-full bg-white border border-terra/40 px-2.5 py-1 text-[11.5px] text-sand-800 transition-colors";
              if (onMissingClick) {
                return (
                  <button
                    key={m.topic}
                    type="button"
                    onClick={() => onMissingClick(m.frage, m.topic)}
                    data-testid={`${testIdPrefix}-missing-${slug}`}
                    className={`${baseCls} hover:border-terra hover:bg-terra/5 cursor-pointer`}
                    title={m.frage}
                  >
                    + {m.topic}
                  </button>
                );
              }
              return (
                <span key={m.topic}
                  data-testid={`${testIdPrefix}-missing-${slug}`}
                  className={baseCls}
                  title={m.frage}>
                  {m.topic}
                </span>
              );
            })}
          </div>
          {onMissingClick && (
            <p className="mt-3 text-[11px] text-sand-500 italic leading-snug">
              Tipp: klicke einen Chip an, antworte im Eingabetext, dann Analyse erneut starten.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
